import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, BookOpen, LifeBuoy, Settings as SettingsIcon } from 'lucide-react';
import { helpAPI } from '../api/client';
import { usePermissions } from '../context/PermissionsContext';

export default function Help() {
  const { hasPermission } = usePermissions();
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    Promise.all([helpAPI.getCategories(), helpAPI.getArticles()])
      .then(([c, a]) => { if (mounted) { setCategories(c.data); setArticles(a.data); } })
      .catch(e => mounted && setError(e.response?.data?.error || e.response?.data?.message || e.message || 'Failed to load Help Center'))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return articles;
    const needle = q.toLowerCase();
    return articles.filter(a =>
      (a.Title || '').toLowerCase().includes(needle) ||
      (a.Summary || '').toLowerCase().includes(needle));
  }, [q, articles]);

  const grouped = useMemo(() => {
    const m = new Map();
    filtered.forEach(a => {
      const key = a.CategoryID;
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(a);
    });
    return m;
  }, [filtered]);

  return (
    <div className="p-6">
      <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white rounded-2xl p-8 mb-6">
        <div className="flex items-center gap-3 mb-2">
          <BookOpen className="w-7 h-7" />
          <h1 className="text-3xl font-bold">Help Center</h1>
        </div>
        <p className="text-blue-100">Step-by-step guides for every part of SpocoDoc.</p>
        <div className="mt-5 relative max-w-xl">
          <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search articles…"
            className="w-full pl-10 pr-4 py-3 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-300"
          />
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          <Link to="/support/new" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg px-3 py-1.5 text-sm">
            <LifeBuoy className="w-4 h-4" /> Contact support
          </Link>
          {hasPermission('manage_help') && (
            <Link to="/help/manage" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg px-3 py-1.5 text-sm">
              <SettingsIcon className="w-4 h-4" /> Manage articles
            </Link>
          )}
        </div>
      </div>

      {loading && <div className="text-gray-500">Loading…</div>}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          <div className="font-semibold">Couldn't load Help Center</div>
          <div className="text-sm mt-1">{error}</div>
          <div className="text-xs mt-2 text-red-600/80">
            If you're an admin: ensure the database script <code>33_Help_Center.sql</code> has been
            run and the backend has been redeployed with the <code>/api/help</code> route.
          </div>
        </div>
      )}

      {!loading && !error && categories.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800">
          No help categories have been seeded yet. Run <code>Database/33_Help_Center.sql</code>.
        </div>
      )}

      {!loading && categories.map(cat => {
        const list = grouped.get(cat.CategoryID) || [];
        if (q && list.length === 0) return null;
        return (
          <section key={cat.CategoryID} className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-1">{cat.Title}</h2>
            {cat.Description && <p className="text-gray-500 text-sm mb-3">{cat.Description}</p>}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {list.length === 0 && (
                <div className="text-sm text-gray-400 italic">No articles yet.</div>
              )}
              {list.map(a => (
                <Link key={a.ArticleID} to={`/help/${a.Slug}`}
                  className="block bg-white border border-gray-200 hover:border-blue-300 hover:shadow-md transition rounded-xl p-4">
                  <div className="font-semibold text-gray-900">{a.Title}</div>
                  {a.Summary && <div className="text-sm text-gray-600 mt-1">{a.Summary}</div>}
                </Link>
              ))}
            </div>
          </section>
        );
      })}

      {!loading && q && filtered.length === 0 && (
        <div className="bg-white border rounded-lg p-6 text-center text-gray-500">
          No articles match “{q}”. Try different keywords or
          <Link to="/support/new" className="text-blue-600 ml-1 underline">open a support ticket</Link>.
        </div>
      )}
    </div>
  );
}
